import api from './client';
import type { WorkshopJob, WorkCharge, CarWindow } from 'types/garage/atelier';

interface ApiCarWindow {
  deposit_date?: string | null;
  deposit_start_hour?: number | null;
  restitution_date?: string | null;
  restitution_start_hour?: number | null;
}

function toFrontendCarWindow(cw: ApiCarWindow): CarWindow {
  return {
    depositDate: cw.deposit_date ?? null,
    depositStartHour: cw.deposit_start_hour ?? null,
    restitutionDate: cw.restitution_date ?? null,
    restitutionStartHour: cw.restitution_start_hour ?? null,
  };
}

// Backend uses snake_case for some fields
interface ApiJob {
  id: string;
  org_id: string;
  client_name: string;
  plate: string;
  hours: number;
  worker_id: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  status: string;
  specialty: string;
  has_car_icon: boolean;
  ordre_id?: string;
  intervention_id?: string;
  car_window?: ApiCarWindow | null;
  auto_generated?: boolean;
  source?: string;
  booking_id?: string;
  vehicle_name?: string;
  interventions?: string[];
  ordre_status?: string;
  myo_local_only?: { reason?: string } | null;
}

function toFrontendJob(j: ApiJob): WorkshopJob {
  return {
    id: j.id,
    clientName: j.client_name,
    plate: j.plate,
    hours: j.hours,
    workerId: j.worker_id,
    date: j.date,
    startHour: j.start_hour,
    durationHours: j.duration_hours,
    status: j.status as WorkshopJob['status'],
    specialty: j.specialty as WorkshopJob['specialty'],
    hasCarIcon: j.has_car_icon,
    ordre_id: j.ordre_id,
    org_id: j.org_id,
    intervention_id: j.intervention_id,
    carWindow: j.car_window ? toFrontendCarWindow(j.car_window) : undefined,
    autoGenerated: j.auto_generated,
    source: j.source,
    bookingId: j.booking_id,
    vehicleName: j.vehicle_name,
    interventions: j.interventions,
    ordreStatus: j.ordre_status,
    myoLocalOnly: j.myo_local_only?.reason || undefined,
  };
}

function toApiJob(j: Partial<WorkshopJob> & { org_id?: string }): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (j.clientName !== undefined) out.client_name = j.clientName;
  if (j.plate !== undefined) out.plate = j.plate;
  if (j.hours !== undefined) out.hours = j.hours;
  if (j.workerId !== undefined) out.worker_id = j.workerId;
  if (j.date !== undefined) out.date = j.date;
  if (j.startHour !== undefined) out.start_hour = j.startHour;
  if (j.durationHours !== undefined) out.duration_hours = j.durationHours;
  if (j.status !== undefined) out.status = j.status;
  if (j.specialty !== undefined) out.specialty = j.specialty;
  if (j.hasCarIcon !== undefined) out.has_car_icon = j.hasCarIcon;
  if (j.ordre_id !== undefined) out.ordre_id = j.ordre_id;
  if (j.intervention_id !== undefined) out.intervention_id = j.intervention_id;
  if (j.org_id !== undefined) out.org_id = j.org_id;
  if (j.autoGenerated !== undefined) out.auto_generated = j.autoGenerated;
  if (j.bookingId !== undefined) out.booking_id = j.bookingId;
  return out;
}

interface ApiCharge {
  id: string;
  org_id: string;
  client_name: string;
  hours: number;
  status: string;
  specialty: string;
  ordre_id?: string;
  car_window?: ApiCarWindow | null;
  auto_generated?: boolean;
  booking_id?: string;
  ordre_status?: string;
  vehicle_name?: string;
  plate?: string;
  servicebox_rdv_id?: string | null;
  evicted?: { reason?: string } | null;
}

function toFrontendCharge(c: ApiCharge): WorkCharge {
  return {
    id: c.id,
    clientName: c.client_name,
    hours: c.hours,
    status: c.status as WorkCharge['status'],
    specialty: c.specialty as WorkCharge['specialty'],
    ordre_id: c.ordre_id,
    org_id: c.org_id,
    plate: c.plate || undefined,
    vehicleName: c.vehicle_name || undefined,
    carWindow: c.car_window ? toFrontendCarWindow(c.car_window) : undefined,
    ordreStatus: c.ordre_status,
    autoGenerated: c.auto_generated,
    bookingId: c.booking_id,
    serviceboxRdvId: c.servicebox_rdv_id || undefined,
    evicted: !!c.evicted || undefined,
  };
}

function toApiCharge(c: Partial<WorkCharge> & { org_id?: string }): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (c.clientName !== undefined) out.client_name = c.clientName;
  if (c.hours !== undefined) out.hours = c.hours;
  if (c.status !== undefined) out.status = c.status;
  if (c.specialty !== undefined) out.specialty = c.specialty;
  if (c.ordre_id !== undefined) out.ordre_id = c.ordre_id;
  if (c.org_id !== undefined) out.org_id = c.org_id;
  if (c.autoGenerated !== undefined) out.auto_generated = c.autoGenerated;
  if (c.bookingId !== undefined) out.booking_id = c.bookingId;
  return out;
}

export type MyoAction = "place" | "move" | "move_split" | "merge" | "split" | "unplan_one" | "unplan_all";

export const atelierApi = {
  // Jobs
  listJobs: async (params?: { org_id?: string; worker_id?: string; date?: string; date_from?: string; date_to?: string; ordre_id?: string; limit?: number; offset?: number; fresh?: boolean }): Promise<WorkshopJob[]> => {
    const { data } = await api.get('/atelier/jobs', { params });
    return (data as ApiJob[]).map(toFrontendJob);
  },

  createJob: async (job: Omit<WorkshopJob, 'id'> & { org_id: string }): Promise<WorkshopJob> => {
    const { data } = await api.post('/atelier/jobs', toApiJob(job));
    return toFrontendJob(data);
  },

  updateJob: async (id: string, updates: Partial<WorkshopJob>): Promise<WorkshopJob> => {
    const { data } = await api.patch(`/atelier/jobs/${id}`, toApiJob(updates));
    return toFrontendJob(data);
  },

  deleteJob: async (id: string): Promise<void> => {
    await api.delete(`/atelier/jobs/${id}`);
  },

  confirmJob: async (id: string): Promise<WorkshopJob> => {
    const { data } = await api.patch(`/atelier/jobs/${id}/confirm`);
    return toFrontendJob(data);
  },

  // Charges
  listCharges: async (params?: { org_id?: string; limit?: number; fresh?: boolean }): Promise<WorkCharge[]> => {
    const { data } = await api.get('/atelier/charges', { params });
    return (data as ApiCharge[]).map(toFrontendCharge);
  },

  createCharge: async (charge: Omit<WorkCharge, 'id'> & { org_id: string }): Promise<WorkCharge> => {
    const { data } = await api.post('/atelier/charges', toApiCharge(charge));
    return toFrontendCharge(data);
  },

  updateCharge: async (id: string, updates: Partial<WorkCharge>): Promise<WorkCharge> => {
    const { data } = await api.patch(`/atelier/charges/${id}`, toApiCharge(updates));
    return toFrontendCharge(data);
  },

  deleteCharge: async (id: string): Promise<void> => {
    await api.delete(`/atelier/charges/${id}`);
  },

  // Écriture dans MyO d'une action faite dans Carvis (créneau ou charge venus de MyO).
  // Le back applique, relit MyO et réaligne les lignes Carvis ; 422 = refus lisible.
  myoAction: async (
    action: MyoAction,
    body: {
      job_id?: string;
      charge_id?: string;
      worker_id?: string;
      date?: string;
      start_hour?: number;
      duration_hours?: number;
      keep_hours?: number;
      after_start_hour?: number;
      /** merge : le morceau fusionné dans job_id (même RDV, même jour). */
      other_job_id?: string;
      /** Hors des horaires du compagnon, confirmé par l'utilisateur (sinon 409 hors_horaires). */
      force?: boolean;
    },
  ): Promise<{
    ok: boolean; created: number; updated: number; removed: number;
    /** Lignes Carvis du RDV relues après l'écriture (documents Firestore bruts). */
    rows?: Record<string, any>[];
    removed_ids?: string[];
    /** merge : bloc Carvis seul absorbé par le magnet MyO (supprimé). */
    absorbed_id?: string;
    /** place : ce qu'il reste de la charge. */
    charge?: { id: string; hours: number; deleted: boolean };
    /** MyO a refusé : enregistré dans Carvis seul, `message` = le motif. */
    local_only?: boolean;
    message?: string;
    /** Magnets qui gênaient dans MyO et qu'on a remis à leur place Carvis. */
    aligned?: { label: string; job_id: string }[];
    /** Magnets qui gênaient, délogés : re-posés ailleurs (replaced) ou « à répartir ». */
    evicted?: { label: string; removed_id?: string | null; replaced?: boolean; date?: string; start_hour?: number }[];
  }> => {
    const { data } = await api.post(`/atelier/myo/${action}`, body, { timeout: 120000 });
    return data;
  },

  // Auto-place
  autoPlaceForOrdre: async (data: {
    org_id: string;
    ordre_id: string;
    interventions: { intervention_id: string; label: string; assigned_to: string; estimated_hours: number }[];
    scoped?: boolean;
  }): Promise<{
    placed: { intervention_id: string; label: string; worker_name: string; status: string; date?: string; start_hour?: number; duration_hours?: number; reason?: string }[];
    unplaced: { intervention_id: string; label: string; worker_name: string; status: string; reason?: string }[];
  }> => {
    // La pose dans MyO relit le planning à chaque créneau : jusqu'à une minute ou deux.
    const { data: result } = await api.post('/atelier/auto-place-for-ordre', data, { timeout: 300000 });
    return result;
  },

  /** Réparation (admin) : travaux Carvis d'un mécanicien classés « autre » → « mecanique ».
   *  `dryRun` : aperçu, rien n'est écrit. */
  retagAutreToMecanique: async (dryRun: boolean): Promise<RetagAutreResult> => {
    const { data } = await api.post('/atelier/admin/retag-autre-to-mecanique', null, {
      params: { dry_run: dryRun },
      timeout: 120000,
    });
    return data;
  },

  /** Réparation (admin) : fond en une seule les charges « à répartir » en double d'un
   *  même ordre (prise de RDV + devis initial). `dryRun` : aperçu, rien n'est écrit. */
  mergeDuplicateCharges: async (dryRun: boolean): Promise<MergeDuplicateChargesResult> => {
    const { data } = await api.post('/atelier/admin/merge-duplicate-charges', null, {
      params: { dry_run: dryRun },
      timeout: 120000,
    });
    return data;
  },
};

export interface RetagAutreItem {
  id: string;
  ordre_id?: string | null;
  ordre_num: string;
  client_name: string;
  plate: string;
  hours: number;
  worker_name?: string;
  date?: string;
  status?: string;
  action?: "reclassee" | "fusionnee";
}

export interface RetagAutreResult {
  dry_run: boolean;
  jobs_count: number;
  charges_count: number;
  charges_merged: number;
  jobs: RetagAutreItem[];
  charges: RetagAutreItem[];
}

export interface MergeDuplicateChargesItem {
  ordre_id: string;
  ordre_num: string;
  client_name: string;
  plate: string;
  specialty: string;
  kept_id: string;
  removed_ids: string[];
  hours_before: number[];
  hours_after: number;
  /** "devis" : travail retenu de l'ordre moins le déjà posé ; "somme" : charges additionnées. */
  basis: "devis" | "somme";
}

export interface MergeDuplicateChargesResult {
  dry_run: boolean;
  ordres_count: number;
  charges_removed: number;
  ordres: MergeDuplicateChargesItem[];
}
